import { HStack, Skeleton } from "@chakra-ui/react";
import { FC, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { IDebtState } from "../../../../../api/debt/types.ts";
import { useGetDebtStateQuery } from "../../../../../api/debt";
import { useGetScheduleQuery } from "../../../../../api/schedule";
import { IScheduleItem } from "../../../../../api/schedule/types.ts";
import { isStringWithNumber, isToday } from "../../../../../utils";
import { PaymentBlockOptionItem } from "./PaymentBlockOptionItem.tsx";

export type SchedulePaymentOptionCodeType = "overdue" | "nearest" | "full";
interface IOption { title: string; amount: string | undefined }
type OptionListType = Record<SchedulePaymentOptionCodeType, IOption>;

interface Props {
  nearestPayment: IScheduleItem | undefined;
  debt: IDebtState | undefined;
  isManualAmount: boolean;
  onSelectOption: (amount: string) => void;
}

export const PaymentBlockOptions: FC<Props> = (props) => {
  const {
    nearestPayment,
    debt,
    isManualAmount,
    onSelectOption,
  } = props;

  const { isFetching: isScheduleFetching } = useGetScheduleQuery();
  const { isFetching: isDebtFetching } = useGetDebtStateQuery();

  const [selectedOption, setSelectedOption] = useState<SchedulePaymentOptionCodeType>();

  const options: OptionListType = useMemo(() => {
    return {
      overdue: {
        title: "Погасить просрочку",
        amount: debt?.overdue?.total,
      },
      nearest: {
        title: "Ближайший платеж",
        amount: isToday(nearestPayment?.date ?? "") ? nearestPayment?.total : "",
      },
      full: {
        title: "Погасить полностью",
        amount: debt?.total,
      },
    };
  }, [debt?.overdue?.total, nearestPayment?.date, nearestPayment?.total, debt?.total]);

  const onChange = (code: SchedulePaymentOptionCodeType | undefined) => {
    setSelectedOption(code);
    onSelectOption(code ? (options[code].amount ?? "") : "");
  };

  useLayoutEffect(() => {
    if (isStringWithNumber(debt?.overdue?.total) && Number(debt?.overdue?.total) > 0) {
      onChange("overdue");
      return;
    }

    if (isToday(nearestPayment?.date ?? "")
      && isStringWithNumber(nearestPayment?.total)
      && Number(nearestPayment?.total) > 0
    ) {
      onChange("nearest");
      return;
    }

    // Deselecting an option and setting empty input
    onChange(undefined);
  }, [debt?.overdue?.total, nearestPayment]);

  useEffect(() => {
    if (isManualAmount) {
      setSelectedOption(undefined);
    }
  }, [isManualAmount]);

  if (isScheduleFetching || isDebtFetching) {
    return (
      <HStack spacing="5px" mb="14px">
        <Skeleton h="32px" w="100px" borderRadius="8px" />
        <Skeleton h="32px" w="100px" borderRadius="8px" />
        <Skeleton h="32px" w="100px" borderRadius="8px" />
      </HStack>
    );
  }

  return (
    <HStack spacing="5px" mb="14px" flexWrap="wrap">
      {Object.entries(options).map(([key, option]) => {
        const k = key as SchedulePaymentOptionCodeType;

        return (
          <PaymentBlockOptionItem
            key={k}
            code={k}
            title={option.title}
            amount={option.amount}
            isSelected={k === selectedOption}
            onClick={() => { onChange(k); }}
          />
        );
      })}
    </HStack>
  );
};
